import React, { useState, useEffect, useRef } from "react";
import { motion, useMotionValue, useTransform, animate, useInView } from "framer-motion";
import { Link } from "react-scroll";
import { FiGithub, FiLinkedin, FiMail, FiBriefcase, FiCalendar } from "react-icons/fi";
import { personalInfo } from "../../data/portfolioData";
import styles from "./Hero.module.css";

const AnimatedStat = ({ value }) => {
  const numMatch = value.match(/\d+/);
  const suffixMatch = value.match(/\D+$/);
  const targetNum = numMatch ? parseInt(numMatch[0]) : 0;
  const suffix = suffixMatch ? suffixMatch[0] : "";

  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, amount: 0.5 });
  
  const count = useMotionValue(0);
  const rounded = useTransform(count, Math.round);

  useEffect(() => {
    if (isInView) {
      // Delaying the animation so it syncs perfectly with the 0.9s parent fade-in delay!
      const animation = animate(count, targetNum, { duration: 2.5, ease: "easeOut", delay: 1.2 });
      return animation.stop;
    }
  }, [count, targetNum, isInView]);

  return (
    <span ref={ref}>
      <motion.span>{rounded}</motion.span>
      {suffix}
    </span>
  );
};

const fadeUpVariants = {
  hidden: { opacity: 0, y: 40 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } },
};

const staggerContainer = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.1 } },
};

const Hero = () => {
  return (
    <section id="home" className={styles.heroSection}>
      <div className={styles.container}>
        <motion.div
          className={styles.contentRow}
          variants={staggerContainer}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.2 }}
        >
          {/* Left Column */}
          <div className={styles.leftCol}>
            <motion.h1 variants={fadeUpVariants} className={styles.h1}>
              <span className={styles.hello}>Hello, I'm</span>
              <br />
              <span className={styles.name}>{personalInfo.name}</span>
            </motion.h1>

            <motion.div
              variants={fadeUpVariants}
              className={styles.subtitleContainer}
            >
              <span className={styles.subtitleStatic}>
                Crafting&nbsp;<span className={styles.highlight}>intelligent systems</span>&nbsp;with&nbsp;<span className={styles.highlight}>3+ years</span>&nbsp;of expertise<span className={styles.pipe}>&nbsp;|&nbsp;</span>
              </span>
              <div className={styles.loader}>
                <div className={styles.words}>
                  <span className={styles.word}>AI Automation Architect</span>
                  <span className={styles.word}>Enterprise AI Solutions Architect</span>
                  <span className={styles.word}>Agentic AI Systems Engineer</span>
                  <span className={styles.word}>Voice AI Platform Engineer</span>
                  <span className={styles.word}>Multi-Cloud AI Architect</span>
                  <span className={styles.word}>LLM & MCP Integration Specialist</span>
                  <span className={styles.word}>AWS Bedrock Solutions Engineer</span>
                  <span className={styles.word}>Generative AI Infrastructure Engineer</span>
                  <span className={styles.word}>Conversational AI Architect</span>
                  <span className={styles.word}>Python AI Engineer</span>
                  <span className={styles.word}>Full Stack Engineer</span>
                  <span className={styles.word}>AI Automation Architect</span>
                </div>
              </div>
            </motion.div>

            <motion.p variants={fadeUpVariants} className={styles.bodyText}>
              Specializing in agentic AI, LLM orchestration, voice AI agents,
              and end-to-end automation workflows that eliminate manual effort.
            </motion.p>

            <motion.div variants={fadeUpVariants} className={styles.actions}>
              <a
                href={personalInfo.resume}
                target="_blank"
                rel="noreferrer"
                className={styles.btnPrimary}
              >
                ↓ Download Resume
              </a>
              <Link
                to="projects"
                smooth={true}
                duration={500}
                className={styles.btnSecondary}
                onClick={() => window.dispatchEvent(new Event('resetProjectsCarousel'))}
              >
                <FiBriefcase size={18} style={{ marginRight: '8px' }} /> View My Work
              </Link>
              <a
                href="https://calendly.com/usmanx191/new-meeting"
                target="_blank"
                rel="noreferrer"
                className={styles.btnSecondary}
              >
                <FiCalendar size={18} style={{ marginRight: '8px' }} /> Book a Meeting
              </a>
            </motion.div>

            <motion.div variants={fadeUpVariants} className={styles.socials}>
              <a
                href={personalInfo.github}
                target="_blank"
                rel="noreferrer"
                aria-label="GitHub"
              >
                <FiGithub size={20} />
              </a>
              <a
                href={personalInfo.linkedin}
                target="_blank"
                rel="noreferrer"
                aria-label="LinkedIn"
              >
                <FiLinkedin size={20} />
              </a>
              <a href={`mailto:${personalInfo.email}`} aria-label="Email">
                <FiMail size={20} />
              </a>
            </motion.div>
          </div>

          {/* Right Column */}
          <div className={styles.rightCol}>
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              viewport={{ once: true }}
              className={styles.imageContainer}
            >
              <div className={styles.imageWrapper}>
                <img
                  src="/profile-icon.png"
                  alt="Usman Mahmood"
                  className={styles.profileImg}
                />
              </div>
              <motion.div
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                transition={{ duration: 0.6, delay: 0.8 }}
                viewport={{ once: true }}
                className={styles.hireBadge}
              >
                <span className={styles.dot}></span>
                Available for Hire
              </motion.div>
              <motion.div
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                transition={{ duration: 0.6, delay: 1.0 }}
                viewport={{ once: true }}
                className={styles.remoteBadge}
              >
                <span className={styles.dot}></span>
                Available for Remote Work
              </motion.div>
            </motion.div>
          </div>
        </motion.div>

        {/* Stats Row */}
        <motion.div
          className={styles.statsRow}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={{
            hidden: {},
            visible: {
              transition: { staggerChildren: 0.1, delayChildren: 0.9 },
            },
          }}
        >
          {personalInfo.stats.map((stat, index) => (
            <motion.div
              key={index}
              variants={fadeUpVariants}
              className={styles.statItem}
            >
              <div className={styles.statValue}>
                <AnimatedStat value={stat.value} />
              </div>
              <div className={styles.statLabel}>{stat.label}</div>
            </motion.div>
          ))}
        </motion.div>
      </div>

      <div className={styles.scrollIndicator}>
        <span className={styles.scrollText}>Scroll to explore</span>
        <div className={styles.arrowDown}>↓</div>
      </div>
    </section>
  );
};

export default Hero;
